/**
 * Shared control-flow for partial cache refresh.
 *
 * The four domain helpers (forecast, variant, collection, npi) all follow the
 * same shape: cancel the in-flight list query, fetch a smaller "by-id" slice,
 * merge it into the cache, fall back to a full invalidate on failure. This
 * helper owns that flow so each domain helper only needs to provide the
 * domain-specific bits (fetch, merge, fallback).
 *
 * Returns nothing — callers are expected to await it inside `onSuccess` so the
 * mutation stays `isPending` until the cache is fresh.
 */
export type PartialRefreshOps<TFetched> = {
  cancel: () => void
  fetch: () => Promise<TFetched | null | undefined>
  apply: (fetched: TFetched) => void
  fallback: () => void
}

export async function runPartialRefresh<TFetched>(ops: PartialRefreshOps<TFetched>): Promise<void> {
  ops.cancel()

  try {
    const fetched = await ops.fetch()
    if (fetched == null) return
    ops.apply(fetched)
  } catch (error) {
    console.error("Partial cache refresh failed, falling back to invalidate:", error)
    ops.fallback()
  }
}

/** Batch runPartialRefresh: fetch all in parallel, apply merges in one tick so React renders once (avoids table flicker). */
export async function runPartialRefreshBatch(opsList: PartialRefreshOps<any>[]): Promise<void> {
  for (const ops of opsList) ops.cancel()

  const results = await Promise.all(
    opsList.map((ops) =>
      ops
        .fetch()
        .then((fetched) => ({ fetched, error: null as unknown }))
        .catch((error: unknown) => ({ fetched: null, error })),
    ),
  )

  // Apply in one tick so React renders once; each apply falls back in isolation.
  results.forEach((result, i) => {
    const ops = opsList[i]!
    if (result.error != null) {
      console.error("Partial cache refresh failed, falling back to invalidate:", result.error)
      ops.fallback()
      return
    }
    if (result.fetched == null) return
    try {
      ops.apply(result.fetched)
    } catch (error) {
      console.error("Partial cache refresh failed, falling back to invalidate:", error)
      ops.fallback()
    }
  })
}
